import AnimatedEllipsis from 'components/utilities/animated-ellipsis'
import classNames from 'helpers/class-names'
import Link from 'next/link'
import {
  Dispatch,
  HTMLAttributes,
  MouseEvent,
  ReactNode,
  RefObject,
  forwardRef,
} from 'react'

interface Props extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  children: ReactNode
  variant?: 'compact' | 'normal' | 'large'
  light?: boolean
  primary?: boolean
  secondary?: boolean
  danger?: boolean
  disabled?: boolean
  isLoading?: boolean
  onClick?: Dispatch<MouseEvent>
  type?: 'button' | 'submit' | 'reset'
  className?: string
  asAnchor?: boolean
  asLink?: boolean
  href?: string
  form?: string
  target?: string
  full?: boolean
}

export const Button = forwardRef(
  (
    {
      variant = 'normal',
      light = false,
      primary = false,
      secondary = false,
      danger = false,
      disabled = false,
      type = 'button',
      className,
      isLoading = false,
      children,
      onClick,
      asAnchor = false,
      asLink = false,
      href = null,
      form = null,
      full = false,
      ...props
    }: Props,
    ref: RefObject<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    if (
      (primary && danger) ||
      (primary && secondary) ||
      (secondary && danger)
    ) {
      throw new Error('Primary and danger can not both be true.')
    }

    if ((light && primary) || (light && danger) || (light && secondary)) {
      throw new Error('Primary, secondary, or danger can not be light.')
    }

    const isLight = light && !disabled
    const isPrimary = primary && !disabled
    const isSecondary = secondary && !disabled
    const isDanger = danger && !disabled

    const buttonClasses = classNames(
      'flex flex-col items-center group rounded outline-none font-semibold',
      'cursor-pointer select-none transition duration-200 ease-in-out',
      'focus-visible:ring-2 focus-visible:ring-info/75',
      'text-gray focus-visible:bg-gray-100',
      {
        'text-white focus-visible:bg-success': isPrimary,
        'focus-visible:bg-gray-50': isLight,
        'text-white focus-visible:bg-danger': isDanger,
        'text-gray-400 focus-visible:bg-white focus-visible:rounded-md':
          isSecondary,
        'text-gray-300 cursor-not-allowed': disabled,
        'text-gray-200': light && disabled,
        'block text-center': asAnchor || asLink,
        'rounded-lg': variant === 'large',
        'w-full': full,
      },
    )

    const clickHandler = (event: React.MouseEvent): void => {
      if (typeof onClick === 'function') {
        if (!asAnchor && !asLink) {
          event.preventDefault()
        }

        onClick(event)
      }
    }

    const ContentWrapper = ({ children }) => (
      <div
        className={classNames(
          'whitespace-nowrap rounded px-3 py-1 font-body text-xs font-semibold tracking-normal',
          'bg-gray-100 group-hover:bg-gray-200/75',
          'transition duration-200 ease-in-out',
          {
            'bg-success group-hover:bg-success-darken group-active:bg-success-darken':
              isPrimary,
            'bg-gray-50 group-hover:bg-gray-100 group-active:bg-gray-100':
              isLight,
            'bg-danger group-hover:bg-danger-darken group-active:bg-danger-darken':
              isDanger,
            'rounded-md border border-gray-100 bg-white group-hover:bg-gray-50 group-active:bg-gray-50':
              isSecondary,
            'group-hover:bg-gray-100': disabled,
            'group-active:scale-95': !disabled,
            'px-1.5 py-px text-2xs': variant === 'compact',
            'rounded-lg px-4 py-2 text-base': variant === 'large',
            'w-full': full,
          },
          className,
        )}>
        {children}
      </div>
    )

    const content = !isLoading ? (
      <ContentWrapper>{children}</ContentWrapper>
    ) : (
      <ContentWrapper>
        <AnimatedEllipsis
          className={classNames({
            'h-2 w-1': variant === 'compact',
            'h-2 w-1.5': variant === 'normal',
            'h-3 w-2': variant === 'large',
          })}
        />
      </ContentWrapper>
    )

    if (asAnchor || asLink) {
      if (href && href.charAt(0) === '/') {
        const anchor = (
          <a
            href={asLink ? null : disabled ? '#' : href}
            aria-disabled={disabled}
            onClick={clickHandler}
            ref={ref as RefObject<HTMLAnchorElement>}
            className={buttonClasses}
            {...props}>
            {content}
          </a>
        )
        return asLink ? (
          <Link passHref href={disabled ? '#' : href}>
            {anchor}
          </Link>
        ) : (
          anchor
        )
      }

      return (
        <a
          href={href}
          onClick={clickHandler}
          ref={ref as RefObject<HTMLAnchorElement>}
          className={buttonClasses}
          {...props}>
          {content}
        </a>
      )
    }

    return (
      <button
        aria-label={isLoading ? 'Loading...' : ''}
        type={type}
        form={type === 'submit' ? form : null}
        ref={ref as RefObject<HTMLButtonElement>}
        onClick={clickHandler}
        className={buttonClasses}
        {...props}
        disabled={disabled}>
        {content}
      </button>
    )
  },
)
